"use client";

import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "@suite/components/icons";
import { cn } from "@suite/lib/utils";

interface Props {
  value: string;
  /** What is being copied, e.g. "snapshot hash" — several copy buttons can share a page, so
   * "Copy" alone leaves screen-reader users guessing which one they're on. */
  label?: string;
  className?: string;
}

export default function CopyButton({ value, label = "value", className }: Props) {
  const [state, setState] = useState<"idle" | "copied" | "error">("idle");
  // Held so a rapid second click can't have the first click's timer clear its confirmation.
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setState("copied");
    } catch {
      // Clipboard can be unavailable (permissions, insecure context), so say so rather than
      // leaving the button looking like nothing happened.
      setState("error");
    }
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setState("idle"), 1600);
  };

  return (
    <>
      <button
        type="button"
        onClick={onCopy}
        aria-label={`Copy ${label}`}
        className={cn(
          "focus-visible:ring-brand-green/70 inline-flex size-6 shrink-0 items-center justify-center rounded-md text-white/70 transition-colors outline-none hover:bg-white/[0.06] hover:text-white focus-visible:ring-2",
          className
        )}
      >
        {state === "copied" ? (
          <Check className="text-brand-green size-3.5" aria-hidden />
        ) : (
          <Copy className="size-3.5" aria-hidden />
        )}
      </button>
      <span role="status" aria-live="polite" className="sr-only">
        {state === "copied"
          ? `${label} copied to clipboard`
          : state === "error"
            ? `Couldn't copy the ${label}`
            : ""}
      </span>
    </>
  );
}
